import React from "react";
import { v4 as uuid } from "uuid";
import { useQuery } from "react-query";

import { PublicVictimItem, PublicVictimSkeleton } from "../PublicVictimItem";
import { publicService } from "../../services/publicService";
import { VICTIMS_LOAD_PER_PAGE } from "../../constants/main";
import { useResolvedLanguage } from "../../hooks/useResolvedLanguage";
import { useBrigades } from "../../hooks/brigades/use-brigades";
import { IBrigade, IVictimBase } from "../../types/common";
import { Picture } from "../Picture";

type Props = {
  query: string;
};

export const VictimsBrigadesList: React.FC<Props> = React.memo(({ query }) => {
  const locale = useResolvedLanguage();

  const {
    data: victims,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["victims", query, locale],
    queryFn: async () => {
      return publicService.getVictims({
        isMilitary: true,
        isBrigades: true,
        query,
        locale,
      });
    },
  });
  const { data: brigades, isLoading: isBrigadesLoading } = useBrigades();

  const brigadeMap = React.useMemo(() => {
    if (!brigades || !victims) return new Map<IBrigade, IVictimBase[]>();

    const map = new Map<IBrigade, typeof victims.data>();

    brigades.forEach((brigade) => {
      map.set(
        brigade,
        victims.data.filter((victim) => victim.brigadeId === brigade.id),
      );
    });

    for (const [brigade, group] of map) {
      if (group.length === 0) {
        map.delete(brigade);
      }
    }

    return map;
  }, [brigades, victims]);

  console.log(brigadeMap);

  return (
    <div className="row">
      {(isLoading || isBrigadesLoading) &&
        Array.from({ length: VICTIMS_LOAD_PER_PAGE }, () => (
          <PublicVictimSkeleton key={uuid()} />
        ))}

      {Array.from(brigadeMap.entries()).map(([brigade, victims]) => (
        <section key={brigade.id} className="mb-2">
          <div className="text-white d-flex align-items-center mb-2">
            {brigade.image && (
              <Picture
                src={brigade.image}
                alt={brigade.name}
                width={48}
                height={48}
                className="me-2"
              />
            )}
            <h5 className="mb-0">{brigade.name}</h5>
          </div>

          <div className="row">
            {victims.map((victim) => (
              <PublicVictimItem key={victim.id} victim={victim} />
            ))}
          </div>
        </section>
      ))}

      {isError && (
        <div className="col">
          <div className="alert alert-danger w-100" role="alert">
            <span className="fs-5">
              Помилка під час завантаження людей
              <i className="bi bi-exclamation-circle ps-3 align-middle" />
            </span>
          </div>
        </div>
      )}
    </div>
  );
});
